import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, Save, Loader2, Info, Zap, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NETWORKS = [
  { key: "mtn", label: "MTN Mobile Money", color: "#FFCB05", ink: "#000" },
  { key: "airtel", label: "Airtel Money", color: "#E60000", ink: "#fff" },
  { key: "zamtel", label: "Zamtel Kwacha", color: "#00A551", ink: "#fff" },
];

export default function AdminPaymentConfig() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  // Manager pricing lives in its own admin doc; kept as separate state so
  // its save/dirty lifecycle doesn't tangle with the payment-config form.
  const [pricing, setPricing] = useState(null);
  const [pricingDirty, setPricingDirty] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [resettingPricing, setResettingPricing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payment-config`);
      setCfg(data);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payment settings");
    }
  }, []);

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/manager-pricing`);
      setPricing(data);
      setPricingDirty(false);
    } catch (e) {
      // Non-fatal — the section stays hidden if this fails.
      // eslint-disable-next-line no-console
      console.warn("manager-pricing load failed", e?.response?.status);
    }
  }, []);

  useEffect(() => { load(); loadPricing(); }, [load, loadPricing]);

  const setField = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const setPricingField = (k, v) => {
    setPricing((p) => ({ ...p, [k]: v }));
    setPricingDirty(true);
  };

  const savePricing = async () => {
    if (!pricing) return;
    const monthly = parseFloat(pricing.monthly_zmw);
    const yearly = parseFloat(pricing.yearly_zmw);
    if (!(monthly > 0) || !(yearly > 0)) {
      toast.error("Both prices must be greater than zero");
      return;
    }
    setPricingSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/manager-pricing`, {
        monthly_zmw: monthly,
        yearly_zmw: yearly,
        features: pricing.features || [],
      });
      setPricing(data);
      setPricingDirty(false);
      toast.success("EasyStay Manager pricing updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save Manager pricing");
    } finally {
      setPricingSaving(false);
    }
  };

  const resetPricing = async () => {
    if (!window.confirm("Reset EasyStay Manager pricing to the default ZMW 500 / 5000 placeholders?")) return;
    setResettingPricing(true);
    try {
      const { data } = await axios.post(`${API}/admin/manager-pricing/reset-defaults`);
      setPricing(data);
      setPricingDirty(false);
      toast.success("Manager pricing reset to defaults");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset Manager pricing");
    } finally {
      setResettingPricing(false);
    }
  };

  // Payment-mode toggle is a separate write path: only the super admin can
  // flip it, and we save it inline (no "unsaved changes" guard) because
  // it's a single-field switch — makes the UI feel instant. Server still
  // clamps to "manual" if Flutterwave keys aren't present, so an over-eager
  // click can never break anything.
  const switchMode = async (nextMode) => {
    if (!cfg || cfg.payment_mode === nextMode) return;
    setSavingMode(true);
    // Optimistic update
    const prev = cfg.payment_mode;
    setCfg((c) => ({ ...c, payment_mode: nextMode }));
    try {
      const { data } = await axios.put(`${API}/admin/payment-config`, {
        mtn_number: cfg.mtn_number || "",
        airtel_number: cfg.airtel_number || "",
        zamtel_number: cfg.zamtel_number || "",
        mtn_name: cfg.mtn_name || "",
        airtel_name: cfg.airtel_name || "",
        zamtel_name: cfg.zamtel_name || "",
        instructions: cfg.instructions || "",
        payment_mode: nextMode,
      });
      setCfg(data);
      if (data.payment_mode === "automatic") {
        toast.success("Automatic mode on — payments now run through Flutterwave");
      } else if (nextMode === "automatic" && data.payment_mode === "manual") {
        // Server clamped — keys missing
        toast.warning("Flutterwave keys not configured — reverted to Manual");
      } else {
        toast.success("Manual mode on — guests send to your Mobile Money numbers");
      }
    } catch (e) {
      setCfg((c) => ({ ...c, payment_mode: prev }));
      toast.error(e.response?.data?.detail || "Could not change payment mode");
    } finally {
      setSavingMode(false);
    }
  };

  const save = async () => {
    if (!cfg) return;
    try {
      setSaving(true);
      const payload = {
        mtn_number: cfg.mtn_number || "",
        airtel_number: cfg.airtel_number || "",
        zamtel_number: cfg.zamtel_number || "",
        mtn_name: cfg.mtn_name || "",
        airtel_name: cfg.airtel_name || "",
        zamtel_name: cfg.zamtel_name || "",
        instructions: cfg.instructions || "",
      };
      const { data } = await axios.put(`${API}/admin/payment-config`, payload);
      setCfg(data);
      setDirty(false);
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-slate-500" data-testid="payment-config-loading">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading payment settings…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="admin-payment-config">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Payment settings</div>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Mobile Money receiving numbers</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Set the phone number and account name guests should send Mobile Money payments to for each Zambian network.
          These are shown to guests at the booking confirmation page once they&apos;ve chosen a network.
        </p>
      </div>

      {/* Payment-mode toggle — only the super admin sees this, and only when
          Flutterwave keys are detected on the server. When keys are missing
          the whole section is hidden and the manual flow continues untouched. */}
      {cfg.flutterwave_available && user?.is_super_admin && (
        <div className="mb-6 rounded-2xl border border-copper/30 bg-gradient-to-br from-copper/5 to-forest/5 p-5 card-shadow" data-testid="payment-mode-toggle">
          <div className="mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-copper" />
            <div className="text-xs font-bold uppercase tracking-widest text-copper">Payment processing mode</div>
          </div>
          <p className="mb-4 text-sm text-slate-600">
            Choose how guests pay for bookings. You can switch back at any time — bookings taken on one mode are unaffected by later changes.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              disabled={savingMode}
              onClick={() => switchMode("manual")}
              data-testid="mode-manual-btn"
              className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition ${
                cfg.payment_mode === "manual"
                  ? "border-forest bg-forest/10 shadow-md"
                  : "border-slate-200 bg-white hover:border-forest/40 hover:bg-forest/5"
              } disabled:opacity-60`}
            >
              <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${cfg.payment_mode === "manual" ? "bg-forest text-white" : "bg-slate-100 text-slate-500"}`}>
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base font-bold text-slate-900">Manual</div>
                <div className="mt-1 text-xs leading-relaxed text-slate-600">
                  Guests see your receiving numbers per network and pay directly. You confirm each payment and mark it as paid.
                </div>
                {cfg.payment_mode === "manual" && (
                  <div className="mt-2 inline-block rounded-full bg-forest px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                    Active
                  </div>
                )}
              </div>
            </button>
            <button
              type="button"
              disabled={savingMode}
              onClick={() => switchMode("automatic")}
              data-testid="mode-automatic-btn"
              className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition ${
                cfg.payment_mode === "automatic"
                  ? "border-copper bg-copper/10 shadow-md"
                  : "border-slate-200 bg-white hover:border-copper/40 hover:bg-copper/5"
              } disabled:opacity-60`}
            >
              <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${cfg.payment_mode === "automatic" ? "bg-copper text-white" : "bg-slate-100 text-slate-500"}`}>
                <Zap className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base font-bold text-slate-900">Automatic <span className="ml-1 text-xs font-normal text-slate-500">(Flutterwave)</span></div>
                <div className="mt-1 text-xs leading-relaxed text-slate-600">
                  Payments are processed in real time through Flutterwave. Guests are confirmed automatically the moment funds settle.
                </div>
                {cfg.payment_mode === "automatic" && (
                  <div className="mt-2 inline-block rounded-full bg-copper px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                    Active
                  </div>
                )}
              </div>
            </button>
          </div>
          {savingMode && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Applying…
            </div>
          )}
        </div>
      )}

      {/* Fallback banner when Flutterwave isn't configured. Only super admins
          see this — regular admins never notice the toggle-vs-not distinction. */}
      {!cfg.flutterwave_available && user?.is_super_admin && (
        <div className="mb-6 flex items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600" data-testid="payment-mode-fallback-notice">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div>
            Automatic payments (Flutterwave) are not configured on this server. Manual mode is active and cannot currently be switched off. Configure <span className="font-mono">FLUTTERWAVE_PUBLIC_KEY</span>, <span className="font-mono">FLUTTERWAVE_SECRET_KEY</span>, and <span className="font-mono">FLUTTERWAVE_ENCRYPTION_KEY</span> on the backend to enable the toggle.
          </div>
        </div>
      )}

      <div className="mb-4 flex items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          Leave a network blank to hide it from the guest checkout. Guests will only see networks that have a receiving number configured.
        </div>
      </div>

      <div className="space-y-4">
        {NETWORKS.map((n) => (
          <div key={n.key} className="rounded-2xl border border-slate-200 bg-white p-5 card-shadow" data-testid={`pc-card-${n.key}`}>
            <div className="mb-3 flex items-center gap-2">
              <span
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
                style={{ background: n.color, color: n.ink }}
              >
                <Smartphone className="h-4 w-4" />
              </span>
              <div className="font-display text-lg font-bold text-slate-900">{n.label}</div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Receiving number</label>
                <input
                  data-testid={`pc-${n.key}-number`}
                  type="tel"
                  value={cfg[`${n.key}_number`] || ""}
                  onChange={(e) => setField(`${n.key}_number`, e.target.value)}
                  placeholder="+260 97X XXX XXX"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Account name (optional)</label>
                <input
                  data-testid={`pc-${n.key}-name`}
                  type="text"
                  value={cfg[`${n.key}_name`] || ""}
                  onChange={(e) => setField(`${n.key}_name`, e.target.value)}
                  placeholder="EasyStay Zambia"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                />
              </div>
            </div>
          </div>
        ))}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 card-shadow">
          <div className="mb-3 font-display text-lg font-bold text-slate-900">Instructions shown to guests</div>
          <textarea
            data-testid="pc-instructions"
            value={cfg.instructions || ""}
            onChange={(e) => setField("instructions", e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Send the exact amount to the number below, then enter your Mobile Money phone number and PIN to confirm."
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
          />
          <div className="mt-1 text-right text-xs text-slate-400">{(cfg.instructions || "").length}/500</div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          {cfg.updated_at ? `Last updated: ${new Date(cfg.updated_at).toLocaleString()}` : "Not saved yet"}
        </div>
        <button
          data-testid="pc-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* EasyStay Manager Pricing — super-admin-only editor. Empty (loading)
          state renders nothing so a non-super-admin who can see the page
          for MM numbers doesn't get a broken section. */}
      {pricing && user?.is_super_admin && (
        <div
          data-testid="admin-manager-pricing"
          className="mt-10 rounded-3xl border border-copper/25 bg-gradient-to-br from-copper/5 to-forest/5 p-6 card-shadow"
        >
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-copper" />
                <div className="text-xs font-bold uppercase tracking-widest text-copper">EasyStay Manager Pricing</div>
              </div>
              <h2 className="mt-1 font-display text-xl font-bold text-slate-900">Subscription rates</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                These prices appear on the "Manage professionally" upsell modal hosts see after publishing a listing. Changes save instantly and take effect on the next upsell view — no code push required.
              </p>
            </div>
            <button
              type="button"
              onClick={resetPricing}
              disabled={resettingPricing || pricingSaving}
              data-testid="admin-manager-pricing-reset"
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {resettingPricing ? "Resetting…" : "Reset to defaults"}
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Monthly (ZMW) *</label>
              <input
                data-testid="admin-manager-pricing-monthly"
                type="number"
                min={1}
                step="0.01"
                value={pricing.monthly_zmw ?? ""}
                onChange={(e) => setPricingField("monthly_zmw", e.target.value)}
                placeholder="500"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-lg font-semibold outline-none focus:border-copper"
              />
              <p className="mt-1 text-[11px] text-slate-500">Placeholder: ZMW 500</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Yearly (ZMW) *</label>
              <input
                data-testid="admin-manager-pricing-yearly"
                type="number"
                min={1}
                step="0.01"
                value={pricing.yearly_zmw ?? ""}
                onChange={(e) => setPricingField("yearly_zmw", e.target.value)}
                placeholder="5000"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-lg font-semibold outline-none focus:border-copper"
              />
              <p className="mt-1 text-[11px] text-slate-500">Placeholder: ZMW 5000</p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {pricing.updated_at ? `Last updated: ${new Date(pricing.updated_at).toLocaleString()}` : "Using placeholder defaults"}
            </div>
            <button
              data-testid="admin-manager-pricing-save"
              onClick={savePricing}
              disabled={pricingSaving || !pricingDirty}
              className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {pricingSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {pricingSaving ? "Saving…" : "Save pricing"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
